import { TextField, Button, Card } from "@mui/material";
import FormFieldErrorClass from "./models/formFieldError";
import ErrorIcon from "@mui/icons-material/Error";
import { useState, useEffect } from "react";
import {
  FormContainer,
  FormSubmitError,
  ActionButtonsContainer,
  HeaderNote,
  InputFieldsWrapper,
  HelperText,
} from "./shared";
import TableHeader from "../Page/tableHeader";
import { ItemLink, TableComponent } from "../Table/dataGridPro";
import styled from "styled-components";
import { useMemo } from "react";
import TableSubHeader from "../Page/tableSubHeader";

const APIErrorContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const AdminNetworkForm = ({
  handleCancel = () => {},
  handleSave = () => {},
  loading = false,
  saving = false,
  error = "",
  organizationsList,
  network,
  isEdit = false,
}) => {
  const tableColumns = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        type: "string",
      },
      {
        headerName: "Total Locations",
        field: "locationsCount",
        type: "number",
      },
      {
        headerName: "Total Tanks",
        field: "tanksCount",
        type: "number",
      },
      {
        headerName: "Total Users",
        field: "usersCount",
        type: "number",
      },
      {
        headerName: "Date Created",
        field: "created",
        type: "dateTime",
        valueGetter: (value, row) => {
          return new Date(value);
        },
      },
    ],
    []
  );

  const userTableColumns = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        type: "string",
        valueGetter: (value, row) => {
          return `${row.firstName} ${row.lastName}`;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink
              to={`/admin/users/${row.id}/edit`}
            >{`${row.firstName} ${row.lastName}`}</ItemLink>
          );
        },
      },
      {
        headerName: "Email Address",
        field: "email",
      },
      {
        headerName: "Phone Number",
        field: "phoneString",
      },
      {
        headerName: "Status",
        field: "status",
      },
      {
        headerName: "Date Created",
        field: "dateCreateObject",
        type: "dateTime",
      },
    ],
    []
  );

  const [save, setSave] = useState(false);
  const [name, setName] = useState("");
  const [organizations, setOrganizations] = useState(organizationsList);
  const [networkOrganizationIds, setNetworkOrganizationIds] = useState([]);
  const [networkUsers, setNetworkUsers] = useState([]);
  const [reloadNetworkUsers, setReloadNetworkUsers] = useState(false);
  const [nameError, setNameError] = useState(new FormFieldErrorClass());
  const [networkOrganizationIdsError, setNetworkOrganizationIdsError] =
    useState(new FormFieldErrorClass());
  const [usersError, setUsersError] = useState(new FormFieldErrorClass());
  const formFieldErrors = [nameError, networkOrganizationIdsError, usersError];

  const [rowSelectionModelInitValues, setRowSelectionModelInitValues] =
    useState([]);

  useEffect(() => {
    if (network?.id) {
      setName(network.name);
      setNetworkOrganizationIds(network.organizationIds);
      setRowSelectionModelInitValues([...network.organizationIds]);
      setNetworkUsers(network.users);
      setReloadNetworkUsers(true);
    }
  }, [network]);

  useEffect(() => {
    if (save) {
      setSave(false);
      if (formFieldErrors.filter((e) => e.hasError).length) {
        return;
      }

      //send data
      handleSave({
        name,
        organizationIds: networkOrganizationIds,
      });
    }
  }, [save, formFieldErrors]);

  const validate = {
    name: (value) => {
      setNameError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setNameError({
          hasError: true,
          message: "Network Name Required",
        });
        return;
      }
      if (value.length > 100) {
        setNameError({
          hasError: true,
          message: "Network Name Max 100 Characters",
        });
        return;
      }
    },
    networkOrganizationIds: (value) => {
      setNetworkOrganizationIdsError({
        hasError: false,
        message: "",
      });
      if (!value || !value?.length) {
        setNetworkOrganizationIdsError({
          hasError: true,
          message: "Minimum 1 Organization Required",
        });
        return;
      }
    },
  };

  const handlers = {
    nameChange: (e) => {
      const value = e.target.value;
      validate.name(value);
      setName(e.target.value);
    },
    networkOrganizationIdsChange: (ids) => {
      const values = [...ids];
      validate.networkOrganizationIds(values);
      setNetworkOrganizationIds(values);
    },
    save: () => {
      //validate all
      validate.name(name);
      validate.networkOrganizationIds(networkOrganizationIds);
      setSave(true);
    },
    cancel: () => {
      handleCancel();
    },
  };

  return (
    <FormContainer>
      <InputFieldsWrapper>
        <div>Network Details</div>
        <div>To create a Network enter the details below</div>
        <HeaderNote>All fields required</HeaderNote>
        <TextField
          id="network-name"
          label="Network Name"
          variant="outlined"
          value={name}
          onChange={handlers.nameChange}
          helperText={nameError.message}
          error={nameError.hasError}
        />

        <Card>
          <TableHeader>
            <div>
              <h2>Organizations</h2>
              <div>
                {networkOrganizationIdsError.hasError && (
                  <HelperText>{networkOrganizationIdsError.message}</HelperText>
                )}
              </div>
            </div>
          </TableHeader>

          <TableComponent
            columns={tableColumns}
            label={""}
            isAdminView={true}
            // reloadData={reloadData}
            placeholder="There are no organizations created yet"
            getRowsFunction={async () => {
              return organizations;
            }}
            checkboxSelection={true}
            initialState={{
              sorting: {
                sortModel: [{ field: "name", sort: "asc" }],
              },
            }}
            rowSelectionCallback={handlers.networkOrganizationIdsChange}
            rowSelectionModelInitValues={rowSelectionModelInitValues}
          />
        </Card>

        {isEdit && (
          <Card>
            <TableHeader>
              <div>
                <h2>Network Users</h2>
                <TableSubHeader>
                  To add or remove Users from a Network go to the{" "}
                  <a href="/admin/users">Users Page</a>
                </TableSubHeader>
              </div>
            </TableHeader>

            <TableComponent
              columns={userTableColumns}
              label={""}
              isAdminView={true}
              reloadData={reloadNetworkUsers}
              placeholder="There are no users in this nework"
              getRowsFunction={async () => {
                return networkUsers;
              }}
              initialState={{
                sorting: {
                  sortModel: [{ field: "name", sort: "asc" }],
                },
              }}
            />
          </Card>
        )}

        <ActionButtonsContainer>
          <Button onClick={handlers.cancel}>Cancel</Button>
          {saving ? (
            <Button disabled variant="contained">
              Saving&#8230;
            </Button>
          ) : (
            <Button onClick={handlers.save} variant="contained">
              Save
            </Button>
          )}
        </ActionButtonsContainer>
        <APIErrorContainer>
          {error && (
            <FormSubmitError>
              <ErrorIcon fontSize="small" />
              {error}
            </FormSubmitError>
          )}
        </APIErrorContainer>
      </InputFieldsWrapper>
    </FormContainer>
  );
};

export default AdminNetworkForm;
